/**
 * Gate 1 — Step 5: Add / Edit Test Case drawer.
 *
 * Same drawer shell as AddRiskDialog / AddMetricDialog.
 * This file hosts both screens:
 *   - Generated / Modified edit  -> GeneratedTestCaseEditDrawer
 *   - Add + Custom edit          -> AddOrCustomTestCaseDialog
 * AddTestCaseDialog routes between them. Do not split into a second file.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactComponent as DownArrowIcon } from "../../asserts/logo/downarrow.svg";
import { ReactComponent as DeleteIcon } from "../../asserts/logo/delete.svg";
import { ReactComponent as WarningTriangleIcon } from "../../asserts/logo/WarningTraingleIcon.svg";
import { ReactComponent as GreenCheckCircleIcon } from "../../asserts/logo/GreenCheckCircle.svg";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";
import { expectedBehaviorFieldLabels } from "../../utils/testCaseExpectedLabels";
import { fetchTestCaseHistory } from "../../services/testCaseClient";
import { trustAttributeLabel } from "../../config/trustAttributes";
import RunWithoutSavingDialog from "../gate2/RunWithoutSavingDialog";

const FLYOUT_STATUS_LABEL = {
  not_reviewed: "Not Validated",
  verified: "Verified",
  flagged: "Flagged",
  deactivated: "Deactivated",
};

const FLYOUT_STATUS_SX = {
  not_reviewed: { bgcolor: "#FFE999", color: "#8A5C00" },
  verified: { bgcolor: "#DCFCE7", color: "#15803D" },
  flagged: { bgcolor: "#FFEDD5", color: "#C2410C" },
  deactivated: { bgcolor: "#F5F5F5", color: "#707070" },
};

function flyoutSourceLabel(source) {
  if (source === "Custom") return "Custom";
  if (source === "Modified") return "Modified";
  return "Generated";
}

function flyoutSourceBadgeSx(source) {
  if (source === "Custom") return { bgcolor: "#FFEDD5", color: "#C2410C" };
  if (source === "Modified") return { bgcolor: "#E6E6E6", color: "#555555" };
  return { bgcolor: "#F5EEFF", color: "#7F2CE8" };
}

function formatFlyoutUpdatedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Same responsive width as AddMetricDialog — fill on xs, 480 sm, 520 md */
const FLYOUT_PAPER_SX = {
  width: { xs: "100%", sm: 480, md: 520 },
  maxWidth: "100vw",
  bgcolor: "#FFFFFF",
  boxShadow: "-8px 0 32px rgba(16,24,40,0.14)",
  boxSizing: "border-box",
};

/** Figma header: vertical, gap 16, pad 16/16/24/16, bottom Grey20 */
const FLYOUT_HEADER_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  pt: "16px",
  pb: "16px",
  pl: "24px",
  pr: "16px",
  bgcolor: "#FFFFFF",
  borderBottom: "1px solid #E6E6E6",
  boxSizing: "border-box",
  minWidth: 0,
};

/** Figma Flag: 24h, radius 12, pad 8, Lato 700/13/24 */
const FLYOUT_FLAG_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "auto",
  minWidth: "fit-content",
  maxWidth: "none",
  height: 24,
  minHeight: 24,
  maxHeight: 24,
  boxSizing: "border-box",
  borderRadius: "12px",
  px: "8px",
  gap: "10px",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  flexShrink: 0,
  overflow: "visible",
  "& .MuiChip-label": {
    px: 0,
    py: 0,
    overflow: "visible",
    textOverflow: "clip",
    whiteSpace: "nowrap",
    fontFamily: "inherit",
    fontWeight: "inherit",
    fontSize: "inherit",
    lineHeight: "inherit",
    letterSpacing: "inherit",
  },
};

const FLYOUT_TC_ID_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  flexShrink: 0,
};

const FLYOUT_TC_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  width: "100%",
  minWidth: 0,
  minHeight: 24,
};

/** Figma Frame 1410120431 — Last updated + close: 200×24 hug, gap 8 */
const FLYOUT_UPDATED_CLUSTER_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "8px",
  width: "auto",
  maxWidth: "100%",
  height: 24,
  minHeight: 24,
  pl: "16px",
  pr: "8px",
  boxSizing: "border-box",
  flexShrink: 0,
  ml: { xs: 0, sm: "auto" },
};

/** Figma timestamp: Lato 12 / 100% / 0 tracking; label 400, date 700 */
const FLYOUT_UPDATED_TEXT_SX = {
  m: 0,
  width: { xs: "auto", sm: 146 },
  maxWidth: "100%",
  height: 14,
  minWidth: 0,
  fontFamily: "Lato, sans-serif",
  fontSize: "12px",
  fontStyle: "normal",
  lineHeight: 1,
  letterSpacing: 0,
  color: "#555555",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Same .Flyout Close Button as AddCustomStrategyDrawer / ModifiedGeneratedStrategyDrawer */
const FLYOUT_CLOSE_BTN_SX = {
  width: 24,
  height: 24,
  p: 0,
  color: "#555555",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { fontSize: 16 },
};

/** Edit flyout header: TC id, badges, last updated */
function EditTestCaseFlyoutHeader({ testCase, onClose, viewOnly = false }) {
  const updatedLabel = formatFlyoutUpdatedAt(testCase.updatedAt || testCase.createdAt);
  const status = testCase.status;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: "8px",
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            minWidth: 0,
            flex: "1 1 auto",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#383838",
              flexShrink: 0,
            }}
          >
            {viewOnly ? "View Test Case" : "Edit Test Case"}
          </Typography>
          <Chip
            label={flyoutSourceLabel(testCase.source)}
            size="small"
            sx={{
              ...FLYOUT_FLAG_SX,
              ...flyoutSourceBadgeSx(testCase.source),
            }}
          />
          {status ? (
            <Chip
              label={FLYOUT_STATUS_LABEL[status] || status}
              size="small"
              sx={{
                ...FLYOUT_FLAG_SX,
                ...(FLYOUT_STATUS_SX[status] || {}),
              }}
            />
          ) : null}
        </Box>
        <Box sx={FLYOUT_UPDATED_CLUSTER_SX}>
          {updatedLabel ? (
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 400,
                fontSize: 12,
                lineHeight: 1,
                letterSpacing: 0,
                color: "#707070",
                whiteSpace: "nowrap",
              }}
            >
              Last updated:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#707070" }}>
                {updatedLabel}
              </Box>
            </Typography>
          ) : null}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={FLYOUT_CLOSE_BTN_SX}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}

const EMPTY = {
  name: "",
  trustAttribute: "",
  testType: "nominal",
  riskId: "",
  metricId: "",
  expected: "",
  prompts: [""],
};

const fieldSx = {
  width: "100%",
  "&.MuiOutlinedInput-root, & .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    opacity: 1,
    gap: "8px",
    px: "10px",
    py: "4px",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    "& fieldset": { border: "1px solid #707070" },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": { border: "1px solid #707070" },
    "&:not(.MuiInputBase-multiline)": { height: 32, minHeight: 32 },
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
    px: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#000000",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#707070",
    opacity: 1,
  },
  "& .MuiSelect-icon": {
    width: 20,
    height: 20,
    right: 6,
    top: "calc(50% - 10px)",
  },
};

/** Editable flyout metadata selects — full column width; label truncates, never resizes the control. */
const METADATA_DROPDOWN_SELECT_SX = {
  ...fieldSx,
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
  boxSizing: "border-box",
  "&.MuiInputBase-root": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
  },
  "& .MuiSelect-select": {
    display: "block",
    width: "100% !important",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
  },
};

const textAreaSx = {
  width: "100%",
  maxWidth: 562,
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "stretch",
    boxSizing: "border-box",
    width: "100%",
    height: "auto",
    minHeight: 51,
    overflow: "hidden",
    resize: "vertical",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    opacity: 1,
    pl: "10px",
    pr: "10px",
    py: "8px",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    "& fieldset": {
      border: "1px solid #707070",
      pointerEvents: "none",
    },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": { border: "1px solid #707070" },
  },
  "& .MuiOutlinedInput-input, & textarea": {
    boxSizing: "border-box",
    flex: 1,
    alignSelf: "stretch",
    width: "100% !important",
    maxWidth: "100%",
    height: "100% !important",
    minHeight: 0,
    margin: 0,
    py: 0,
    px: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#000000",
    resize: "none",
    overflow: "auto",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#707070",
    opacity: 1,
  },
};

const READ_ONLY_TEXTAREA_SX = {
  ...textAreaSx,
  "& .MuiOutlinedInput-root": {
    ...textAreaSx["& .MuiOutlinedInput-root"],
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    boxShadow: "none",
    resize: "none",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const fieldLabelSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 14,
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
};

const cancelBtnSx = {
  boxSizing: "border-box",
  width: 77,
  minWidth: 77,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  backgroundImage:
    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
  color: "#555555",
  boxShadow: "0px 1px 2px 0px #00000014",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  transition: "none",
  "&:hover": {
    border: "1px solid #BBBBBB",
    backgroundImage:
      "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(0deg, #F5F5F5, #F5F5F5)",
    boxShadow: "0px 1px 2px 0px #00000014",
  },
};

const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const primaryBtnSx = {
  boxSizing: "border-box",
  minWidth: 122,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundImage: PRIMARY_BG,
  border: "1px solid transparent",
  boxShadow: "0px 1px 2px 0px #0000000A",
  transition: "none",
  "&:hover": {
    color: "#FFFFFF",
    bgcolor: "transparent",
    backgroundImage: PRIMARY_BG,
    boxShadow: "0px 1px 2px 0px #0000000A",
  },
  "&.Mui-disabled": {
    color: "#005BF0",
    bgcolor: "#FFFFFF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    boxShadow: "0px 1px 2px 0px #0000000A",
    opacity: 1,
  },
};

function FieldLabel({ required, children }) {
  return (
    <Typography sx={{ ...fieldLabelSx, mb: 0.75 }}>
      {required && (
        <Typography component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
          *
        </Typography>
      )}
      {children}
    </Typography>
  );
}

/** Figma Textfield — Test Case Title only: fill width, hug height, 8px gap */
const TITLE_FIELD_WRAP_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

/** Figma .Input label Small — Lato 700 / 14 / 21, Neutral/Grey50, no required mark */
const TITLE_FIELD_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
  width: "100%",
  minWidth: 0,
  height: 21,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Figma Input Container: fill, hug 32, radius 4, pad 4/10 — editable (white + border) */
const TITLE_FIELD_SX = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    width: "100%",
    minWidth: 0,
    height: 32,
    minHeight: 32,
    maxHeight: 32,
    boxSizing: "border-box",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    backgroundColor: "#FFFFFF",
    opacity: 1,
    gap: "8px",
    px: "10px",
    py: "4px",
    alignItems: "center",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    "& fieldset": { border: "1px solid #707070" },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": { border: "1px solid #707070" },
  },
  "& .MuiOutlinedInput-input": {
    p: 0,
    height: 24,
    boxSizing: "border-box",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&::placeholder": {
      color: "#707070",
      opacity: 1,
    },
  },
};

/** View-only title field — grey fill, no border */
const TITLE_FIELD_READ_ONLY_SX = {
  ...TITLE_FIELD_SX,
  "& .MuiOutlinedInput-root": {
    ...TITLE_FIELD_SX["& .MuiOutlinedInput-root"],
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    boxShadow: "none",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

function FlyoutTruncatedTitleField({ id, value, readOnly, onChange, sx }) {
  const inputRef = useRef(null);
  const [tipOpen, setTipOpen] = useState(false);

  const checkTruncated = () => {
    if (!value) {
      setTipOpen(false);
      return;
    }
    const el = inputRef.current;
    setTipOpen(!!(el && el.scrollWidth > el.clientWidth));
  };

  return (
    <Tooltip
      title={value || ""}
      arrow
      placement="bottom"
      open={tipOpen}
      onClose={() => setTipOpen(false)}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={FLYOUT_TOOLTIP_SLOT_PROPS}
      describeChild
    >
      <TextField
        id={id}
        fullWidth
        hiddenLabel
        placeholder="e.g., Routing number disclosure validation"
        value={value}
        onChange={onChange}
        inputRef={inputRef}
        InputProps={{ readOnly: readOnly }}
        onMouseEnter={checkTruncated}
        onMouseLeave={() => setTipOpen(false)}
        sx={sx}
      />
    </Tooltip>
  );
}

/** Figma Frame 1410120389 — Trust Attribute + peer dropdowns: 16px gap, fill width */
const DROPDOWN_ROW_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: "16px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const DROPDOWN_COL_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: { xs: "1 1 auto", sm: "1 1 0" },
  width: { xs: "100%", sm: "auto" },
  minWidth: 0,
  boxSizing: "border-box",
};

/** Figma Trust Attribute Select — same Input Container as title field + downarrow.svg */
const DROPDOWN_SELECT_SX = {
  width: "100%",
  minWidth: 0,
  height: 32,
  minHeight: 32,
  maxHeight: 32,
  boxSizing: "border-box",
  borderRadius: "4px",
  bgcolor: "#F5F5F5",
  backgroundColor: "#F5F5F5",
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "16px",
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#000000",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    height: 32,
    minHeight: 32,
    py: "4px",
    pl: "10px",
    pr: "42px !important",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    width: 24,
    height: 24,
    right: 10,
    top: "calc(50% - 12px)",
    color: "#000000",
    flexShrink: 0,
  },
};

/** Risk selected value — ellipsis before the arrow; same box size as other dropdowns */
const RISK_SELECT_SX = {
  ...DROPDOWN_SELECT_SX,
  "& .MuiSelect-select": {
    ...DROPDOWN_SELECT_SX["& .MuiSelect-select"],
    display: "block",
  },
};

/** Same Figma tooltip as Test Cases list — black pill, Lato 12/18 */
const FLYOUT_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "#000000",
      borderRadius: "4px",
      px: "12px",
      py: "12px",
      fontFamily: "Lato, sans-serif",
      fontWeight: 400,
      fontSize: 12,
      lineHeight: "18px",
      letterSpacing: 0,
      color: "#E6E6E6",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
      maxWidth: 288,
      wordBreak: "break-word",
      "& .MuiTooltip-arrow": { color: "#000000" },
    },
  },
};

/** Figma flyout tooltip — only when content is clipped (single- or multi-line). */
function FlyoutEllipsisTooltip({ title, children, checkMultiline = false }) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const fullTitle = String(title ?? "").trim();

  const measure = () => {
    if (!fullTitle) {
      setOpen(false);
      return;
    }
    const root = rootRef.current;
    if (!root) {
      setOpen(false);
      return;
    }
    const target =
      root.querySelector("textarea") ||
      root.querySelector("input") ||
      root.querySelector(".MuiTypography-root") ||
      root;
    const truncated = checkMultiline
      ? target.scrollHeight > target.clientHeight + 1 || target.scrollWidth > target.clientWidth
      : target.scrollWidth > target.clientWidth;
    setOpen(truncated);
  };

  if (!fullTitle) {
    return children;
  }

  return (
    <Tooltip
      title={fullTitle}
      arrow
      placement="bottom"
      open={open}
      onClose={() => setOpen(false)}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={FLYOUT_TOOLTIP_SLOT_PROPS}
      describeChild
    >
      <Box
        ref={rootRef}
        onMouseEnter={measure}
        onMouseLeave={() => setOpen(false)}
        sx={{ minWidth: 0, width: "100%" }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

function FlyoutSelectTruncationTooltip({ displayTitle, children }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const fullTitle = String(displayTitle ?? "").trim();

  const measure = useCallback(() => {
    if (!fullTitle) {
      setOpen(false);
      return;
    }
    const el = wrapRef.current?.querySelector(".MuiSelect-select");
    setOpen(!!(el && el.scrollWidth > el.clientWidth));
  }, [fullTitle]);

  useEffect(() => {
    if (!open) return undefined;
    const dismiss = () => setOpen(false);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open]);

  const shell = (
    <Box
      ref={wrapRef}
      onMouseEnter={fullTitle ? measure : undefined}
      onMouseLeave={fullTitle ? () => setOpen(false) : undefined}
      sx={{ minWidth: 0, width: "100%", maxWidth: "100%" }}
    >
      {children}
    </Box>
  );

  if (!fullTitle) {
    return shell;
  }

  return (
    <Tooltip
      title={fullTitle}
      arrow
      placement="bottom"
      open={open}
      onClose={() => setOpen(false)}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      slotProps={FLYOUT_TOOLTIP_SLOT_PROPS}
      describeChild
    >
      {shell}
    </Tooltip>
  );
}

function FlyoutDropdown({ id, label, displayTitle, tooltipTitle, selectSx, readOnly, children, ...selectProps }) {
  const resolvedTitle = displayTitle ?? tooltipTitle ?? "";
  const select = (
    <Select
      id={id}
      displayEmpty
      IconComponent={DownArrowIcon}
      sx={selectSx || DROPDOWN_SELECT_SX}
      readOnly={readOnly}
      open={readOnly ? false : undefined}
      onOpen={readOnly ? (event) => event.preventDefault() : undefined}
      onMouseDown={readOnly ? (event) => event.preventDefault() : undefined}
      onKeyDown={
        readOnly
          ? (event) => {
              if ([" ", "Enter", "ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                event.preventDefault();
              }
            }
          : undefined
      }
      inputProps={{ readOnly: Boolean(readOnly) }}
      {...selectProps}
    >
      {children}
    </Select>
  );

  const selectWithTooltip = resolvedTitle ? (
    <FlyoutSelectTruncationTooltip displayTitle={resolvedTitle}>{select}</FlyoutSelectTruncationTooltip>
  ) : (
    select
  );

  return (
    <Box sx={DROPDOWN_COL_SX}>
      <Typography component="label" htmlFor={id} sx={TITLE_FIELD_LABEL_SX}>
        {label}
      </Typography>
      <FormControl fullWidth sx={{ minWidth: 0, width: "100%" }}>
        {selectWithTooltip}
      </FormControl>
    </Box>
  );
}

/** Figma Traceability card — Risk + Metric only; fill width, hug height */
const TRACEABILITY_CARD_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  p: "16px",
  borderRadius: "8px",
  border: "1px solid #E8E8E8",
  bgcolor: "#FFFFFF",
};

const TRACEABILITY_HEADING_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: 1,
  letterSpacing: 0,
  color: "#333333",
  width: "100%",
  minWidth: 0,
  height: 17,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const TRACEABILITY_STEP_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  width: "100%",
  minWidth: 0,
};

const TRACEABILITY_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "10px",
  lineHeight: 1,
  letterSpacing: 0,
  textTransform: "uppercase",
  color: "#8D8D8D",
  width: "100%",
  minWidth: 0,
  height: 12,
};

const TRACEABILITY_VALUE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: "13px",
  lineHeight: "18px",
  letterSpacing: 0,
  color: "#707070",
  width: "100%",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

function TraceabilityValue({ name, code }) {
  if (!name && !code) {
    return <Typography sx={TRACEABILITY_VALUE_SX}>—</Typography>;
  }
  const fullText = `${name || "—"}${code ? ` : ${code}` : ""}`;
  return (
    <FlyoutEllipsisTooltip title={fullText}>
      <Typography sx={TRACEABILITY_VALUE_SX} component="div">
        <Box component="span" sx={{ fontWeight: 400 }}>
          {name || "—"}
          {code ? " : " : ""}
        </Box>
        {code ? (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {code}
          </Box>
        ) : null}
      </Typography>
    </FlyoutEllipsisTooltip>
  );
}

/** Figma Ground Truth wrap: heading + Grey10 panel */
const GROUND_TRUTH_WRAP_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
  minWidth: 0,
};

const GROUND_TRUTH_PANEL_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  p: "16px",
  borderRadius: "4px",
  bgcolor: "#F5F5F5",
};

const groundTruthCardSx = (accent) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  pt: "12px",
  pr: "12px",
  pb: "12px",
  pl: "16px",
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  border: "1px solid #E6E6E6",
  borderLeft: `3px solid ${accent}`,
});

const GROUND_TRUTH_LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "11px",
  lineHeight: 1,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  width: "fit-content",
};

const GROUND_TRUTH_INPUT_SX = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    p: 0,
    bgcolor: "transparent",
    alignItems: "flex-start",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
  "& textarea": {
    p: 0,
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: 1.4,
    letterSpacing: 0,
    color: "#555555",
  },
};

const GROUND_TRUTH_FOOTER_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "flex-end" },
  justifyContent: "space-between",
  gap: "16px",
  width: "100%",
  minWidth: 0,
  minHeight: 40,
};

/** Figma Frame 1410120102 — heading + helper: fill, hug, gap 8 */
const PROMPT_SECTION_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const PROMPT_HEADING_WRAP_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

/** Figma helper: Lato 400 / 14 / 21, Neutral/Grey50 */
const PROMPT_HELPER_SX = {
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
  width: "100%",
  minWidth: 0,
  minHeight: 21,
};

/** Figma Frame 1410120387 — prompt card: fill, hug 80, gap 8 */
const PROMPT_CARD_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

/** Figma Frame 1410120388 — label + delete: fill, hug 24, gap 8 */
const PROMPT_LABEL_ROW_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  height: 24,
  minHeight: 24,
  boxSizing: "border-box",
};

const PROMPT_LABEL_SX = {
  ...TITLE_FIELD_LABEL_SX,
  flex: "1 1 auto",
  width: "auto",
};

const PROMPT_DELETE_BTN_SX = {
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  p: 0,
  flexShrink: 0,
  color: "#DE350B",
  "& svg": { width: 16, height: 18, display: "block" },
  "&.Mui-disabled": {
    opacity: 0.38,
    color: "#DE350B",
  },
};

/** Prompt textarea — fill width, grow with content, vertical drag-resize */
const PROMPT_FIELD_SX = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    width: "100%",
    minWidth: 0,
    alignItems: "flex-start",
    boxSizing: "border-box",
    minHeight: 48,
    height: "auto",
    padding: "8px 10px",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    backgroundColor: "#FFFFFF",
    overflow: "visible",
    fontFamily: "Lato, sans-serif",
    fontSize: 16,
    fontWeight: 400,
    lineHeight: "24px",
    color: "#000000",
    boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
    "& fieldset": { border: "1px solid #707070", borderWidth: "1px !important" },
    "&:hover fieldset": { border: "1px solid #707070", borderWidth: "1px !important" },
    "&.Mui-focused": { boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)" },
    "&.Mui-focused fieldset": { border: "1px solid #707070", borderWidth: "1px !important" },
  },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea": {
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    minHeight: 24,
    maxHeight: "none",
    margin: 0,
    padding: "0 !important",
    fontFamily: "Lato, sans-serif",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: 0,
    color: "#000000",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    resize: "vertical !important",
    overflowX: "hidden",
    overflowY: "auto",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#000000",
    opacity: 0.45,
  },
};

/** Figma Ghost Primary Medium — same states as Test Strategy footer ghost */
const GHOST_PRIMARY_BTN_SX = {
  alignSelf: "flex-start",
  boxSizing: "border-box",
  width: "fit-content",
  maxWidth: "100%",
  minWidth: 0,
  height: 32,
  minHeight: 32,
  px: "16px",
  py: "4px",
  gap: "4px",
  borderRadius: "4px",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  border: "1px solid #005BF0",
  color: "#005BF0",
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)",
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

const PROMPT_SECTION_LINE_SX = {
  width: "100%",
  minWidth: 0,
  height: 0,
  border: 0,
  borderTop: "1px solid #E6E6E6",
  boxSizing: "border-box",
};

const HISTORY_FIELD_LABELS = {
  name: "Title",
  expected_output: "Expected",
  input: "Prompts",
  testType: "Test type",
  trustAttribute: "Trust attribute",
  metricId: "Linked metric",
  status: "Status",
};

const HISTORY_FIELD_ORDER = [
  "name",
  "expected_output",
  "input",
  "testType",
  "trustAttribute",
  "metricId",
  "status",
];

const HISTORY_TEST_TYPE_LABELS = {
  nominal: "Functional",
  functional: "Functional",
  edge: "Edge",
  adversarial: "Adversarial",
  failure_mode: "Failure mode",
};

/** Figma History accordion. The count is the number of saved edits, newest first. */
const HISTORY_WRAP_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  p: "16px",
  borderRadius: "4px",
  bgcolor: "#F5F5F5",
};

const HISTORY_ROW_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  height: 21,
  minHeight: 21,
};

const HISTORY_LABEL_SX = {
  m: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#707070",
  whiteSpace: "nowrap",
};

function formatHistoryWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatHistoryValue(field, value, metrics) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return "empty";
  if (field === "input" && Array.isArray(value)) return value.join(" | ");
  if (field === "testType") return HISTORY_TEST_TYPE_LABELS[value] || value;
  if (field === "status") return FLYOUT_STATUS_LABEL[value] || value;
  if (field === "trustAttribute") return trustAttributeLabel(value);
  if (field === "metricId") {
    const metric = (metrics || []).find((item) => item.id === value);
    return metric?.metricName || value;
  }
  return String(value);
}

function TestCaseHistorySection({ projectId, testCaseId, metrics, expectedLabel }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!projectId || !testCaseId) {
      setHistory([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setOpen(false);
    fetchTestCaseHistory(projectId, testCaseId)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.history;
        setHistory(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, testCaseId]);

  const fieldLabels = {
    ...HISTORY_FIELD_LABELS,
    expected_output: expectedLabel || HISTORY_FIELD_LABELS.expected_output,
  };

  return (
    <Box sx={HISTORY_WRAP_SX}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        sx={{
          ...HISTORY_ROW_SX,
          border: 0,
          p: 0,
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {open ? (
          <ExpandMoreIcon sx={{ width: 16, height: 16, fontSize: 16, color: "#707070", flexShrink: 0 }} />
        ) : (
          <ChevronRightIcon sx={{ width: 16, height: 16, fontSize: 16, color: "#707070", flexShrink: 0 }} />
        )}
        <Typography sx={HISTORY_LABEL_SX}>History ({history.length})</Typography>
      </Box>
      {open ? (
        <Stack spacing={1.5}>
          {history.length === 0 ? (
            <Typography sx={{ ...HISTORY_LABEL_SX, fontWeight: 400, whiteSpace: "normal" }}>
              No earlier edits.
            </Typography>
          ) : (
            history.map((entry, index) => {
              const changes = entry?.changes || {};
              const fields = HISTORY_FIELD_ORDER.filter((field) => changes[field]);
              return (
                <Box key={`${entry?.savedAt || "edit"}-${index}`} sx={{ minWidth: 0 }}>
                  <Typography sx={{ ...HISTORY_LABEL_SX, fontWeight: 700 }}>
                    {formatHistoryWhen(entry?.savedAt) || "Earlier edit"}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {fields.map((field) => (
                      <Typography
                        key={field}
                        sx={{
                          m: 0,
                          fontFamily: "Lato, sans-serif",
                          fontWeight: 400,
                          fontSize: "13px",
                          lineHeight: "20px",
                          color: "#383838",
                          wordBreak: "break-word",
                        }}
                      >
                        {fieldLabels[field]}: {formatHistoryValue(field, changes[field].from, metrics)} →{" "}
                        {formatHistoryValue(field, changes[field].to, metrics)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              );
            })
          )}
        </Stack>
      ) : null}
    </Box>
  );
}

/** Same footer tokens as AddCustomStrategyDrawer / ModifiedGeneratedStrategyDrawer */
const FOOTER_PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const FOOTER_PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const FOOTER_PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const FOOTER_BTN_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.04)";
const FOOTER_BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const FOOTER_CANCEL_SHADOW = "0px 1px 2px 0px rgba(0, 0, 0, 0.08)";
const FOOTER_CANCEL_BG =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), #F5F5F5";

const FLYOUT_FOOTER_SX = {
  flexShrink: 0,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  minHeight: 88,
  px: { xs: 2, sm: "24px" },
  py: { xs: 2, sm: "24px" },
  bgcolor: "#F5F5F5",
  borderTop: "1px solid #E6E6E6",
  display: "flex",
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  gap: { xs: 1.5, sm: "16px" },
  flexDirection: { xs: "column", sm: "row" },
};

const FLYOUT_FOOTER_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  flex: { sm: "1 1 310px" },
  minWidth: 0,
};

const FLYOUT_FOOTER_BTN_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
};

const FLYOUT_CANCEL_BTN_SX = {
  ...FLYOUT_FOOTER_BTN_TEXT_SX,
  boxSizing: "border-box",
  height: 40,
  minHeight: 40,
  minWidth: 0,
  width: { xs: "100%", sm: "fit-content" },
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  borderColor: "#BBBBBB",
  backgroundColor: "#F5F5F5",
  backgroundImage: FOOTER_CANCEL_BG,
  color: "#555555",
  boxShadow: FOOTER_CANCEL_SHADOW,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  justifyContent: "center",
  alignItems: "center",
  "&:hover": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: FOOTER_CANCEL_BG,
    color: "#555555",
    boxShadow: FOOTER_CANCEL_SHADOW,
  },
  "&:focus, &:focus-visible": {
    border: "1px solid #BBBBBB",
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: FOOTER_CANCEL_BG,
    color: "#555555",
    boxShadow: FOOTER_CANCEL_SHADOW,
  },
  "&.Mui-disabled": {
    border: "1px solid #BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: FOOTER_CANCEL_BG,
    color: "#555555",
    opacity: 0.5,
  },
};

const FLYOUT_PRIMARY_BTN_SX = {
  ...FLYOUT_FOOTER_BTN_TEXT_SX,
  height: 40,
  minHeight: 40,
  minWidth: 0,
  width: { xs: "100%", sm: "fit-content" },
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  backgroundImage: FOOTER_PRIMARY_BG,
  boxShadow: FOOTER_BTN_SHADOW,
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    backgroundImage: FOOTER_PRIMARY_BG_HOVER,
    boxShadow: FOOTER_BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${FOOTER_PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: FOOTER_BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${FOOTER_PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${FOOTER_PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: FOOTER_BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: 1,
    cursor: "not-allowed",
  },
};

/** Figma Alerts Small: fill, 48h, radius 8, pad 8, gap 8, no border */
const PROMPT_ALERT_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  minWidth: 0,
  height: { xs: "auto", sm: 48 },
  minHeight: 48,
  boxSizing: "border-box",
  p: "8px",
  borderRadius: "8px",
  border: "none",
};

const PROMPT_ALERT_WARNING_SX = {
  ...PROMPT_ALERT_SX,
  bgcolor: "#FFF8E0",
  color: "#8A5C00",
};

const PROMPT_ALERT_SUCCESS_SX = {
  ...PROMPT_ALERT_SX,
  bgcolor: "#E8FCF3",
  color: "#00754E",
};

const PROMPT_ALERT_ICON_WRAP_SX = {
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": { display: "block" },
};

const PROMPT_ALERT_TEXT_SX = {
  m: 0,
  flex: "1 1 auto",
  minWidth: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontStyle: "normal",
  fontSize: "14px",
  lineHeight: "21px",
  letterSpacing: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: { xs: "normal", sm: "nowrap" },
};

const PROMPT_ALERT_ACTION_SX = {
  flexShrink: 0,
  minWidth: 0,
  height: 32,
  minHeight: 32,
  boxSizing: "border-box",
  px: "16px",
  py: "4px",
  gap: "4px",
  borderRadius: "4px",
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  verticalAlign: "middle",
  border: "2px solid transparent",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: "none",
  boxShadow: "none",
  "&:hover:not(.Mui-disabled):not(:disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    textDecoration: "underline",
    boxShadow: "none",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: "none !important",
    border: "2px solid #005FCC",
    textDecoration: "none",
  },
  "&:active:not(.Mui-disabled):not(:disabled)": {
    bgcolor: "#F0F6FF !important",
    backgroundColor: "#F0F6FF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    textDecoration: "none",
    boxShadow: "none",
  },
  "&.Mui-disabled, &:disabled": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: "2px solid transparent",
    color: "#BBBBBB",
    textDecoration: "none",
    boxShadow: "none",
    cursor: "not-allowed",
  },
  "& .MuiTouchRipple-root": { display: "none" },
};

const PROMPT_ALERT_CLOSE_SX = {
  ...FLYOUT_CLOSE_BTN_SX,
  color: "inherit",
};

function PromptInlineAlert({ variant, message, actionLabel, onAction, onClose }) {
  const isWarning = variant === "warning";
  return (
    <Box role="alert" sx={isWarning ? PROMPT_ALERT_WARNING_SX : PROMPT_ALERT_SUCCESS_SX}>
      <Box sx={PROMPT_ALERT_ICON_WRAP_SX} aria-hidden>
        {isWarning ? <WarningTriangleIcon /> : <GreenCheckCircleIcon />}
      </Box>
      <Typography sx={{ ...PROMPT_ALERT_TEXT_SX, color: "inherit" }}>{message}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
        <Button
          disableRipple
          disableElevation
          onClick={onAction}
          sx={{
            ...PROMPT_ALERT_ACTION_SX,
            color: isWarning ? "#8A5C00" : "#00754E",
          }}
        >
          {actionLabel}
        </Button>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={PROMPT_ALERT_CLOSE_SX}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function metricLabel(metric) {
  const code = metric.metricId ? metric.metricId.slice(0, 12).toUpperCase() : metric.id.slice(0, 8).toUpperCase();
  return `${metric.metricName} (${code})`;
}

function metricNameOnly(metric) {
  return metric?.metricName || "—";
}

function linkedMetricRenderValue(metrics, nameOnly) {
  if (!nameOnly) return undefined;
  return (selected) => {
    if (!selected) return "";
    const metric = metrics.find((m) => m.id === selected);
    return metricNameOnly(metric);
  };
}

const GATE2_FLYOUT_TRUST_PLACEHOLDER = "e.g., Accuracy and performance";
const GATE2_FLYOUT_RISK_PLACEHOLDER = "e.g., Hallucination";
const GATE2_FLYOUT_METRIC_PLACEHOLDER = "e.g., Hallucination rate (MET001)";

function AddOrCustomTestCaseDialog({
  open,
  onClose,
  onSave,
  onRunOnce,
  metrics = [],
  risks = [],
  editingTestCase = null,
  viewOnly = false,
  isContentGenerator = false,
  flyoutVariant = "default",
  projectId = null,
}) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [infoBannerOpen, setInfoBannerOpen] = useState(true);
  const [runOnceConfirmOpen, setRunOnceConfirmOpen] = useState(false);
  const metadataReadOnly = Boolean(editingTestCase);
  const contentReadOnly = viewOnly;
  const metadataSelectSx = viewOnly ? DROPDOWN_SELECT_SX : METADATA_DROPDOWN_SELECT_SX;
  const expectedLabels = expectedBehaviorFieldLabels(isContentGenerator);
  const isGate2Add = flyoutVariant === "gate2Custom" && !editingTestCase;

  const riskById = useMemo(() => new Map(risks.map((r) => [r.id, r])), [risks]);
  const attributes = useMemo(
    () => Array.from(new Set(metrics.map((m) => m.trustAttribute).filter(Boolean))),
    [metrics]
  );
  const metricCodeById = useMemo(() => {
    const sorted = [...metrics].sort((a, b) => (a.metricName || "").localeCompare(b.metricName || ""));
    return new Map(
      sorted.map((m, i) => [
        m.id,
        m.metricId
          ? String(m.metricId).slice(0, 12).toUpperCase()
          : `MET${String(i + 1).padStart(3, "0")}`,
      ])
    );
  }, [metrics]);

  useEffect(() => {
    if (!open) {
      setRunOnceConfirmOpen(false);
      return;
    }
    if (editingTestCase) {
      const metric = metrics.find((m) => m.id === editingTestCase.metricId);
      setForm({
        name: editingTestCase.name || "",
        trustAttribute: editingTestCase.trustAttribute || "",
        testType: editingTestCase.testType || "nominal",
        riskId: editingTestCase.riskId || metric?.riskId || "",
        metricId: editingTestCase.metricId || "",
        expected: editingTestCase.groundTruth?.expected_output || editingTestCase.groundTruth?.expected_behavior || "",
        prompts: editingTestCase.input?.length ? [...editingTestCase.input] : [""],
      });
    } else {
      setForm(EMPTY);
      setInfoBannerOpen(true);
    }
  }, [open, editingTestCase, metrics]);

  const risksForAttribute = useMemo(() => {
    if (!form.trustAttribute) return risks;
    return risks.filter((r) => r.attribute === form.trustAttribute);
  }, [risks, form.trustAttribute]);

  const metricsForSelection = useMemo(() => {
    let list = metrics;
    if (form.trustAttribute) {
      list = list.filter((m) => m.trustAttribute === form.trustAttribute);
    }
    if (form.riskId) {
      list = list.filter((m) => m.riskId === form.riskId);
    }
    return list;
  }, [metrics, form.trustAttribute, form.riskId]);

  const selectedMetric = metrics.find((m) => m.id === form.metricId) || null;

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleAttributeChange = (e) => {
    const trustAttribute = e.target.value;
    setForm((f) => {
      const nextRisks = risks.filter((r) => r.attribute === trustAttribute);
      const stillValidRisk = nextRisks.some((r) => r.id === f.riskId);
      const riskId = stillValidRisk ? f.riskId : "";
      const nextMetrics = metrics.filter(
        (m) => m.trustAttribute === trustAttribute && (!riskId || m.riskId === riskId)
      );
      const stillValidMetric = nextMetrics.some((m) => m.id === f.metricId);
      return {
        ...f,
        trustAttribute,
        riskId,
        metricId: stillValidMetric ? f.metricId : "",
      };
    });
  };

  const handleRiskChange = (e) => {
    const riskId = e.target.value;
    const risk = riskById.get(riskId);
    setForm((f) => {
      const trustAttribute = risk?.attribute || f.trustAttribute;
      const stillValidMetric = metrics.some(
        (m) => m.id === f.metricId && m.riskId === riskId
      );
      return {
        ...f,
        riskId,
        trustAttribute,
        metricId: stillValidMetric ? f.metricId : "",
      };
    });
  };

  const handleMetricChange = (e) => {
    const metricId = e.target.value;
    const metric = metrics.find((m) => m.id === metricId);
    setForm((f) => ({
      ...f,
      metricId,
      trustAttribute: metric?.trustAttribute || f.trustAttribute,
      riskId: metric?.riskId || f.riskId,
    }));
  };

  const handlePromptChange = (idx, value) => {
    setForm((f) => ({ ...f, prompts: f.prompts.map((p, i) => (i === idx ? value : p)) }));
  };

  const validPrompts = form.prompts.map((p) => p.trim()).filter(Boolean);
  const canSave = Boolean(
    form.name.trim() &&
      form.trustAttribute &&
      form.riskId &&
      form.metricId &&
      form.expected.trim() &&
      form.prompts.length > 0 &&
      form.prompts.every((p) => p.trim())
  );

  const buildSavePayload = () => ({
    name: form.name.trim(),
    trust_attribute: form.trustAttribute,
    risk_id: form.riskId,
    metric_id: form.metricId,
    expected_output: form.expected.trim(),
    fail_criteria: "",
    input: validPrompts,
  });

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSubmitting(true);
      await onSave(buildSavePayload());
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunOnce = async () => {
    if (!canSave) return;
    const payload = buildSavePayload();
    const runner = onRunOnce || onSave;
    try {
      setSubmitting(true);
      await runner(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const openRunOnceConfirm = () => {
    if (!canSave || submitting) return;
    setRunOnceConfirmOpen(true);
  };

  const closeRunOnceConfirm = () => {
    if (submitting) return;
    setRunOnceConfirmOpen(false);
  };

  /** Gate 2 — dismiss modal (if open) and close the add-custom flyout back to execution UI. */
  const dismissGate2CustomFlyout = () => {
    if (submitting) return;
    setRunOnceConfirmOpen(false);
    onClose();
  };

  const handleDrawerClose = (_event, reason) => {
    if (isGate2Add && runOnceConfirmOpen) {
      if (reason === "escapeKey") {
        closeRunOnceConfirm();
      }
      return;
    }
    if (isGate2Add) {
      dismissGate2CustomFlyout();
      return;
    }
    onClose();
  };

  const handleRunOnceConfirm = async () => {
    setRunOnceConfirmOpen(false);
    await handleRunOnce();
  };

  const FLYOUT_GHOST_PRIMARY_FOOTER_BTN_SX = {
    ...GHOST_PRIMARY_BTN_SX,
    ...FLYOUT_FOOTER_BTN_TEXT_SX,
    height: 40,
    minHeight: 40,
    fontSize: 15,
    alignSelf: "auto",
  };

  const closeFlyoutUi = isGate2Add ? dismissGate2CustomFlyout : onClose;

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      PaperProps={{
        sx: FLYOUT_PAPER_SX,
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(52,64,84,0.45)" } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={FLYOUT_HEADER_SX}>
          {editingTestCase ? (
            <EditTestCaseFlyoutHeader
              testCase={editingTestCase}
              onClose={closeFlyoutUi}
              viewOnly={viewOnly}
            />
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography sx={FLYOUT_TC_TITLE_SX}>
                  {isGate2Add ? "Add Custom Test Case" : "Add Test Case"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={closeFlyoutUi}
                  aria-label="Close"
                  sx={{
                    width: 24,
                    height: 24,
                    p: "5.25px",
                    color: "#555555",
                    opacity: 1,
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon sx={{ width: 16, height: 16, display: "block" }} />
                </IconButton>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "24px",
                  letterSpacing: 0,
                  color: "#555555",
                  mt: 0.75,
                }}
              >
                Link to an approved metric. Ground truth is validated at creation.
              </Typography>
              {!isGate2Add && infoBannerOpen ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: "#EFF8FF",
                    color: "#0046B8",
                  }}
                >
                  <InfoOutlinedIcon sx={{ width: 20, height: 20, color: "#0046B8", bgcolor: "#F0F6FF", borderRadius: "50%" }} />
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: "21px",
                      letterSpacing: 0,
                      verticalAlign: "middle",
                      color: "#0046B8",
                      flex: 1,
                    }}
                  >
                    New test cases are validated at creation and not subject to system review.
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setInfoBannerOpen(false)}
                    aria-label="Dismiss info"
                    sx={{
                      width: 24,
                      height: 24,
                      p: "5.25px",
                      color: "#0046B8",
                      opacity: 1,
                      flexShrink: 0,
                      ml: "auto",
                    }}
                  >
                    <CloseIcon sx={{ width: 16, height: 16, display: "block" }} />
                  </IconButton>
                </Box>
              ) : null}
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", pl: "24px", pr: "16px", py: "16px", minWidth: 0, bgcolor: "#FFFFFF" }}>
          <Stack spacing={2.5}>
            <Box sx={TITLE_FIELD_WRAP_SX}>
              <Typography component="label" htmlFor="test-case-title" sx={TITLE_FIELD_LABEL_SX}>
                <Typography component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
                  *
                </Typography>
                Test Case Title
              </Typography>
              <FlyoutTruncatedTitleField
                id="test-case-title"
                value={form.name}
                readOnly={contentReadOnly}
                onChange={contentReadOnly ? undefined : handle("name")}
                sx={contentReadOnly ? TITLE_FIELD_READ_ONLY_SX : TITLE_FIELD_SX}
              />
            </Box>

            <Box sx={DROPDOWN_ROW_SX}>
              <Box sx={DROPDOWN_COL_SX}>
                <FieldLabel required>Trust Attribute</FieldLabel>
                <FlyoutSelectTruncationTooltip
                  displayTitle={
                    form.trustAttribute ? trustAttributeLabel(form.trustAttribute) : ""
                  }
                >
                  <FormControl fullWidth size="small" sx={{ width: "100%", minWidth: 0 }}>
                    <Select
                      IconComponent={DropdownIcon}
                      value={form.trustAttribute}
                      onChange={metadataReadOnly ? undefined : handleAttributeChange}
                      displayEmpty
                      disabled={metadataReadOnly}
                      renderValue={(selected) =>
                        selected
                          ? trustAttributeLabel(selected)
                          : isGate2Add
                            ? GATE2_FLYOUT_TRUST_PLACEHOLDER
                            : "Select attribute"
                      }
                      sx={{
                        ...metadataSelectSx,
                        ...(!form.trustAttribute && { "& .MuiSelect-select": { color: "#707070" } }),
                      }}
                    >
                      {attributes.map((a) => (
                        <MenuItem key={a} value={a}>{trustAttributeLabel(a)}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FlyoutSelectTruncationTooltip>
              </Box>
              <Box sx={DROPDOWN_COL_SX}>
                <FieldLabel required>Risk</FieldLabel>
                <FlyoutSelectTruncationTooltip
                  displayTitle={
                    form.riskId
                      ? risksForAttribute.find((r) => r.id === form.riskId)?.name ||
                        risks.find((r) => r.id === form.riskId)?.name ||
                        ""
                      : ""
                  }
                >
                  <FormControl fullWidth size="small" sx={{ width: "100%", minWidth: 0 }}>
                    <Select
                      value={form.riskId}
                      onChange={metadataReadOnly ? undefined : handleRiskChange}
                      displayEmpty
                      disabled={metadataReadOnly}
                      IconComponent={DropdownIcon}
                      renderValue={(selected) => {
                        if (!selected) {
                          return isGate2Add ? GATE2_FLYOUT_RISK_PLACEHOLDER : "Select risk";
                        }
                        const risk =
                          risksForAttribute.find((r) => r.id === selected) ||
                          risks.find((r) => r.id === selected);
                        return risk?.name ?? "";
                      }}
                      sx={{
                        ...metadataSelectSx,
                        ...(!form.riskId && { "& .MuiSelect-select": { color: "#707070" } }),
                      }}
                    >
                      {risksForAttribute.map((r) => (
                        <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FlyoutSelectTruncationTooltip>
              </Box>
            </Box>

            <Box sx={{ width: "100%", minWidth: 0 }}>
              <FieldLabel required>Linked Metric</FieldLabel>
              <FlyoutSelectTruncationTooltip
                displayTitle={
                  selectedMetric
                    ? editingTestCase
                      ? metricNameOnly(selectedMetric)
                      : metricLabel(selectedMetric)
                    : ""
                }
              >
                <FormControl fullWidth size="small" sx={{ width: "100%", minWidth: 0 }}>
                  <Select
                    IconComponent={DropdownIcon}
                    value={form.metricId}
                    onChange={metadataReadOnly ? undefined : handleMetricChange}
                    displayEmpty
                    disabled={metadataReadOnly}
                    renderValue={(selected) => {
                      if (!selected) {
                        return GATE2_FLYOUT_METRIC_PLACEHOLDER;
                      }
                      const metric = metricsForSelection.find((m) => m.id === selected);
                      if (!metric) return "";
                      return editingTestCase ? metricNameOnly(metric) : metricLabel(metric);
                    }}
                    sx={{
                      ...metadataSelectSx,
                      ...(!form.metricId && { "& .MuiSelect-select": { color: "#707070" } }),
                    }}
                  >
                    {metricsForSelection.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {editingTestCase ? metricNameOnly(m) : metricLabel(m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FlyoutSelectTruncationTooltip>
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: 12,
                  lineHeight: "18px",
                  letterSpacing: 0,
                  color: "#707070",
                  mt: 0.5,
                }}
              >
                Traceability is created automatically based on the linked metric.
              </Typography>
            </Box>

            {isGate2Add ? (
              <>
                <Box>
                  <Typography sx={{ ...fieldLabelSx, mb: 0.5 }}>Test Input / Prompt</Typography>
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: "21px",
                      letterSpacing: 0,
                      color: "#707070",
                      mb: 1,
                    }}
                  >
                    Each prompt tests the same Expected Behavior — vary input phrasing to probe edge cases.
                  </Typography>
                  <Stack spacing={1.25}>
                    {form.prompts.map((p, idx) => (
                      <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <FieldLabel required>Prompt</FieldLabel>
                        <FlyoutEllipsisTooltip title={contentReadOnly ? p : ""} checkMultiline>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder="Enter test prompt..."
                            value={p}
                            onChange={contentReadOnly ? undefined : (e) => handlePromptChange(idx, e.target.value)}
                            InputProps={{ readOnly: contentReadOnly }}
                            sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : textAreaSx}
                          />
                        </FlyoutEllipsisTooltip>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <FieldLabel required>{expectedLabels.field}</FieldLabel>
                  <FlyoutEllipsisTooltip title={contentReadOnly ? form.expected : ""} checkMultiline>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={3}
                      placeholder={expectedLabels.placeholder}
                      value={form.expected}
                      onChange={contentReadOnly ? undefined : handle("expected")}
                      InputProps={{ readOnly: contentReadOnly }}
                      sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : textAreaSx}
                    />
                  </FlyoutEllipsisTooltip>
                </Box>

                <TestCaseHistorySection
                  projectId={projectId}
                  testCaseId={editingTestCase?.id}
                  metrics={metrics}
                  expectedLabel={expectedLabels.field}
                />
              </>
            ) : (
              <>
                <Box>
                  <Typography sx={{ ...fieldLabelSx, mb: 1 }}>Ground Truth</Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <FieldLabel required>{expectedLabels.field}</FieldLabel>
                      <FlyoutEllipsisTooltip title={contentReadOnly ? form.expected : ""} checkMultiline>
                        <TextField
                          fullWidth
                          required
                          multiline
                          minRows={1}
                          placeholder={expectedLabels.placeholder}
                          value={form.expected}
                          onChange={contentReadOnly ? undefined : handle("expected")}
                          InputProps={{ readOnly: contentReadOnly }}
                          sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : textAreaSx}
                        />
                      </FlyoutEllipsisTooltip>
                    </Box>
                    <Box sx={GROUND_TRUTH_FOOTER_SX}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                        <Typography sx={TRACEABILITY_LABEL_SX}>SME Status</Typography>
                        <Chip
                          label={
                            editingTestCase
                              ? (FLYOUT_STATUS_LABEL[editingTestCase.status] || editingTestCase.status)
                              : "Not Validated"
                          }
                          size="small"
                          sx={{
                            ...FLYOUT_FLAG_SX,
                            ...(editingTestCase
                              ? (FLYOUT_STATUS_SX[editingTestCase.status] || FLYOUT_STATUS_SX.not_reviewed)
                              : FLYOUT_STATUS_SX.not_reviewed),
                            width: "fit-content",
                          }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          minWidth: 0,
                          alignItems: { xs: "flex-start", sm: "flex-end" },
                          textAlign: { xs: "left", sm: "right" },
                        }}
                      >
                        <Typography sx={TRACEABILITY_LABEL_SX}>Scored against</Typography>
                        <TraceabilityValue
                          name={selectedMetric?.metricName}
                          code={editingTestCase ? "" : (selectedMetric ? metricCodeById.get(selectedMetric.id) : "")}
                        />
                      </Box>
                    </Box>
                  </Stack>
                </Box>

            <Box>
              <Typography sx={{ ...fieldLabelSx, mb: 0.5 }}>
                <Typography component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
                  *
                </Typography>
                Test Input / Prompt
              </Typography>
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontWeight: 400, fontStyle: "normal", fontSize: 14, lineHeight: "21px", letterSpacing: 0, color: "#707070", mb: 1 }}>
                {expectedLabels.helper}
              </Typography>
              <Stack spacing={1.25}>
                {form.prompts.map((p, idx) => (
                  <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Typography sx={fieldLabelSx}>
                      Prompt
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      placeholder="Enter test prompt..."
                      value={p}
                      onChange={contentReadOnly ? undefined : (e) => handlePromptChange(idx, e.target.value)}
                      InputProps={{ readOnly: contentReadOnly }}
                      sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : textAreaSx}
                    />
                  </Box>
                ))}
              </Stack>
              <Box sx={PROMPT_SECTION_LINE_SX} />
              <Box sx={{ mt: "16px" }}>
                <TestCaseHistorySection
                  projectId={projectId}
                  testCaseId={editingTestCase?.id}
                  metrics={metrics}
                  expectedLabel={expectedLabels.field}
                />
              </Box>
            </Box>
              </>
            )}
          </Stack>
        </Box>

        {isGate2Add && !viewOnly ? (
          <Box sx={{ ...FLYOUT_FOOTER_SX, justifyContent: "flex-end" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "flex-end",
                gap: 1.5,
                flexShrink: 0,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Button
                disableElevation
                disableRipple
                variant="outlined"
                onClick={dismissGate2CustomFlyout}
                sx={FLYOUT_CANCEL_BTN_SX}
              >
                Cancel
              </Button>
              <Button
                disableElevation
                disableRipple
                variant="outlined"
                onClick={openRunOnceConfirm}
                disabled={submitting || !canSave}
                sx={FLYOUT_GHOST_PRIMARY_FOOTER_BTN_SX}
              >
                Run Once
              </Button>
              <Button
                disableElevation
                disableRipple
                variant="contained"
                onClick={handleSave}
                disabled={submitting || !canSave}
                sx={FLYOUT_PRIMARY_BTN_SX}
              >
                Save & Execute
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#F5F5F5",
              borderTop: "1px solid #EAECF0",
              display: "flex",
              alignItems: "center",
              justifyContent: viewOnly ? "flex-end" : "space-between",
              gap: 2,
            }}
          >
            {!viewOnly ? (
              <Typography
                sx={{
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: 13,
                  lineHeight: "24px",
                  letterSpacing: 0,
                  color: "#555555",
                  flex: 1,
                }}
              >
                {editingTestCase
                  ? "Changes apply to this test case immediately after save."
                  : "Test case will be created with confirmed status."}
              </Typography>
            ) : null}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button disableElevation disableRipple variant="outlined" onClick={onClose} sx={FLYOUT_CANCEL_BTN_SX}>
                {viewOnly ? "Close" : "Cancel"}
              </Button>
              {!viewOnly ? (
                <Button
                  disableElevation
                  disableRipple
                  variant="contained"
                  onClick={handleSave}
                  disabled={submitting || !canSave}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: "#005BF0",
                    borderRadius: "4px",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
                  }}
                >
                  {editingTestCase ? "Save changes" : "Add test case"}
                </Button>
              ) : null}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
    {isGate2Add ? (
      <RunWithoutSavingDialog
        open={runOnceConfirmOpen}
        onClose={closeRunOnceConfirm}
        onConfirm={handleRunOnceConfirm}
        submitting={submitting}
      />
    ) : null}
    </>
  );
}

function GeneratedTestCaseEditDrawer({
  open,
  onClose,
  onSave,
  metrics = [],
  risks = [],
  editingTestCase = null,
  viewOnly = false,
  isContentGenerator = false,
  projectId = null,
}) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [promptDeleteConfirmIdx, setPromptDeleteConfirmIdx] = useState(null);
  const [promptUndo, setPromptUndo] = useState(null);
  const expectedLabels = expectedBehaviorFieldLabels(isContentGenerator);

  const riskById = useMemo(() => new Map(risks.map((r) => [r.id, r])), [risks]);
  const attributes = useMemo(
    () => Array.from(new Set(metrics.map((m) => m.trustAttribute).filter(Boolean))),
    [metrics]
  );

  useEffect(() => {
    if (!open) return;
    setPromptDeleteConfirmIdx(null);
    setPromptUndo(null);
    if (editingTestCase) {
      setForm({
        name: editingTestCase.name || "",
        trustAttribute: editingTestCase.trustAttribute || "",
        testType: editingTestCase.testType || "nominal",
        riskId: editingTestCase.riskId || "",
        metricId: editingTestCase.metricId || "",
        expected: editingTestCase.groundTruth?.expected_output || editingTestCase.groundTruth?.expected_behavior || "",
        prompts: editingTestCase.input?.length ? [...editingTestCase.input] : [""],
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editingTestCase]);

  const metadataReadOnly = Boolean(editingTestCase);
  const contentReadOnly = viewOnly;
  const selectedMetric = metrics.find((m) => m.id === form.metricId) || null;
  const linkedRisk = selectedMetric ? riskById.get(selectedMetric.riskId) : null;

  const metricCodeById = useMemo(() => {
    const sorted = [...metrics].sort((a, b) => (a.metricName || "").localeCompare(b.metricName || ""));
    return new Map(
      sorted.map((m, i) => [
        m.id,
        m.metricId
          ? String(m.metricId).slice(0, 12).toUpperCase()
          : `MET${String(i + 1).padStart(3, "0")}`,
      ])
    );
  }, [metrics]);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleMetricChange = (e) => {
    const metricId = e.target.value;
    const metric = metrics.find((m) => m.id === metricId);
    setForm((f) => ({
      ...f,
      metricId,
      trustAttribute: metric?.trustAttribute || f.trustAttribute,
      riskId: metric?.riskId || f.riskId,
    }));
  };

  const handlePromptChange = (idx, value) => {
    setForm((f) => ({ ...f, prompts: f.prompts.map((p, i) => (i === idx ? value : p)) }));
  };

  const requestRemovePrompt = (idx) => {
    if (form.prompts.length <= 1) return;
    if (!String(form.prompts[idx] || "").trim()) return;
    setPromptUndo(null);
    setPromptDeleteConfirmIdx(idx);
  };

  const cancelRemovePrompt = () => setPromptDeleteConfirmIdx(null);

  const confirmRemovePrompt = () => {
    if (promptDeleteConfirmIdx == null || form.prompts.length <= 1) {
      setPromptDeleteConfirmIdx(null);
      return;
    }
    const idx = promptDeleteConfirmIdx;
    const value = form.prompts[idx];
    if (!String(value || "").trim()) {
      setPromptDeleteConfirmIdx(null);
      return;
    }
    setForm((f) => ({ ...f, prompts: f.prompts.filter((_, i) => i !== idx) }));
    setPromptDeleteConfirmIdx(null);
    setPromptUndo({ index: idx, value });
  };

  const undoRemovePrompt = () => {
    if (!promptUndo) return;
    setForm((f) => {
      const next = [...f.prompts];
      next.splice(promptUndo.index, 0, promptUndo.value);
      return { ...f, prompts: next };
    });
    setPromptUndo(null);
  };

  const dismissPromptRemovedToast = () => setPromptUndo(null);

  const validPrompts = form.prompts.map((p) => p.trim()).filter(Boolean);
  const canSave =
    form.name.trim() &&
    form.trustAttribute &&
    form.metricId &&
    form.expected.trim() &&
    validPrompts.length > 0;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSubmitting(true);
      await onSave({
        name: form.name.trim(),
        trust_attribute: form.trustAttribute,
        test_type: form.testType,
        metric_id: form.metricId,
        expected_output: form.expected.trim(),
        fail_criteria: "",
        input: validPrompts,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: FLYOUT_PAPER_SX,
      }}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(52,64,84,0.45)" } } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={FLYOUT_HEADER_SX}>
          {editingTestCase ? (
            <EditTestCaseFlyoutHeader
              testCase={editingTestCase}
              onClose={onClose}
              viewOnly={viewOnly}
            />
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#101828" }}>
                  Add Test Case
                </Typography>
                <IconButton
                  size="small"
                  onClick={onClose}
                  aria-label="Close"
                  sx={{ color: "#555", mt: -0.5, mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 13, color: "#667085", mt: 0.75 }}>
                Link to an approved metric. Ground truth is validated at creation.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "#EFF8FF",
                  color: "#026AA2",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                  New test cases are validated at creation and not subject to system review.
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", pl: "24px", pr: "16px", py: "16px", minWidth: 0, bgcolor: "#FFFFFF" }}>
          <Stack spacing={2.5}>
            <Box sx={TITLE_FIELD_WRAP_SX}>
              <Typography component="label" htmlFor="generated-test-case-title" sx={TITLE_FIELD_LABEL_SX}>
                Test Case Title
              </Typography>
              <FlyoutTruncatedTitleField
                id="generated-test-case-title"
                value={form.name}
                readOnly={contentReadOnly}
                onChange={contentReadOnly ? undefined : handle("name")}
                sx={contentReadOnly ? TITLE_FIELD_READ_ONLY_SX : TITLE_FIELD_SX}
              />
            </Box>

            <Box sx={DROPDOWN_ROW_SX}>
              <FlyoutDropdown
                id="generated-trust-attribute"
                label="Trust Attribute"
                value={form.trustAttribute}
                onChange={metadataReadOnly ? undefined : handle("trustAttribute")}
                readOnly={metadataReadOnly}
                displayTitle={
                  form.trustAttribute ? trustAttributeLabel(form.trustAttribute) : ""
                }
              >
                <MenuItem value="" disabled>
                  Select attribute
                </MenuItem>
                {attributes.map((a) => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </FlyoutDropdown>
              <FlyoutDropdown
                id="generated-risk"
                label="Risk"
                value={form.riskId}
                onChange={metadataReadOnly ? undefined : handle("riskId")}
                displayTitle={riskById.get(form.riskId)?.name || ""}
                selectSx={RISK_SELECT_SX}
                readOnly={metadataReadOnly}
              >
                <MenuItem value="" disabled>
                  Select risk
                </MenuItem>
                {risks.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </FlyoutDropdown>
            </Box>

            <Box sx={{ width: "100%", minWidth: 0 }}>
              <FlyoutDropdown
                id="generated-linked-metric"
                label="Linked Metric"
                value={form.metricId}
                onChange={metadataReadOnly ? undefined : handleMetricChange}
                readOnly={metadataReadOnly}
                renderValue={linkedMetricRenderValue(metrics, Boolean(editingTestCase))}
                displayTitle={selectedMetric ? metricNameOnly(selectedMetric) : ""}
              >
                <MenuItem value="" disabled>
                  e.g., Hallucination rate (MET001)
                </MenuItem>
                {metrics.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {editingTestCase ? metricNameOnly(m) : metricLabel(m)}
                  </MenuItem>
                ))}
              </FlyoutDropdown>
            </Box>

            <Box sx={TRACEABILITY_CARD_SX}>
              <Typography sx={TRACEABILITY_HEADING_SX}>Traceability</Typography>
              <Box sx={TRACEABILITY_STEP_SX}>
                <Typography sx={TRACEABILITY_LABEL_SX}>Risk</Typography>
                <TraceabilityValue name={linkedRisk?.name} />
              </Box>
              <Box sx={TRACEABILITY_STEP_SX}>
                <Typography sx={TRACEABILITY_LABEL_SX}>Metric</Typography>
                <TraceabilityValue
                  name={selectedMetric?.metricName}
                  code={editingTestCase ? "" : (selectedMetric ? metricCodeById.get(selectedMetric.id) : "")}
                />
              </Box>
            </Box>

            <Box sx={GROUND_TRUTH_WRAP_SX}>
              <Typography sx={TITLE_FIELD_LABEL_SX}>Ground Truth</Typography>
              <Box sx={GROUND_TRUTH_PANEL_SX}>
                <Box sx={groundTruthCardSx("#14B872")}>
                  <Typography sx={{ ...GROUND_TRUTH_LABEL_SX, color: "#14B872" }}>
                    {expectedLabels.field}
                  </Typography>
                  <FlyoutEllipsisTooltip title={contentReadOnly ? form.expected : ""} checkMultiline>
                    <TextField
                      fullWidth
                      multiline
                      minRows={1}
                      placeholder={expectedLabels.placeholder}
                      value={form.expected}
                      onChange={contentReadOnly ? undefined : handle("expected")}
                      InputProps={{ readOnly: contentReadOnly }}
                      sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : GROUND_TRUTH_INPUT_SX}
                    />
                  </FlyoutEllipsisTooltip>
                </Box>
                <Box sx={GROUND_TRUTH_FOOTER_SX}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                    <Typography sx={TRACEABILITY_LABEL_SX}>SME Status</Typography>
                    <Chip
                      label={
                        editingTestCase
                          ? (FLYOUT_STATUS_LABEL[editingTestCase.status] || editingTestCase.status)
                          : "Not Validated"
                      }
                      size="small"
                      sx={{
                        ...FLYOUT_FLAG_SX,
                        ...(editingTestCase
                          ? (FLYOUT_STATUS_SX[editingTestCase.status] || FLYOUT_STATUS_SX.not_reviewed)
                          : FLYOUT_STATUS_SX.not_reviewed),
                        width: "fit-content",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      minWidth: 0,
                      alignItems: { xs: "flex-start", sm: "flex-end" },
                      textAlign: { xs: "left", sm: "right" },
                    }}
                  >
                    <Typography sx={TRACEABILITY_LABEL_SX}>Scored against</Typography>
                    <TraceabilityValue
                      name={selectedMetric?.metricName}
                      code={editingTestCase ? "" : (selectedMetric ? metricCodeById.get(selectedMetric.id) : "")}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                height: 0,
                border: 0,
                borderTop: "1px solid #E6E6E6",
                boxSizing: "border-box",
              }}
            />

            <Box sx={PROMPT_SECTION_SX}>
              <Box sx={PROMPT_HEADING_WRAP_SX}>
                <Typography sx={TITLE_FIELD_LABEL_SX}>Test Input / Prompt</Typography>
                <Typography sx={PROMPT_HELPER_SX}>
                  {expectedLabels.helper}
                </Typography>
              </Box>
              <Stack spacing="16px">
                {form.prompts.map((p, idx) => (
                  <Box key={idx} sx={PROMPT_CARD_SX}>
                    <Box sx={PROMPT_LABEL_ROW_SX}>
                      <Typography sx={PROMPT_LABEL_SX}>Prompt</Typography>
                      {!viewOnly && form.prompts.length > 1 ? (
                        <IconButton
                          size="small"
                          aria-label={`Delete prompt ${idx + 1}`}
                          disabled={!String(p || "").trim()}
                          onClick={() => requestRemovePrompt(idx)}
                          sx={PROMPT_DELETE_BTN_SX}
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null}
                    </Box>
                    <FlyoutEllipsisTooltip title={contentReadOnly ? p : ""} checkMultiline>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        placeholder="Enter test prompt..."
                        value={p}
                        onChange={contentReadOnly ? undefined : (e) => handlePromptChange(idx, e.target.value)}
                        InputProps={{ readOnly: contentReadOnly }}
                        sx={contentReadOnly ? READ_ONLY_TEXTAREA_SX : textAreaSx}
                      />
                    </FlyoutEllipsisTooltip>
                    {!viewOnly && promptDeleteConfirmIdx === idx && String(p || "").trim() ? (
                      <PromptInlineAlert
                        variant="warning"
                        message="Remove this prompt? This action cannot be undone."
                        actionLabel="Remove"
                        onAction={confirmRemovePrompt}
                        onClose={cancelRemovePrompt}
                      />
                    ) : null}
                  </Box>
                ))}
              </Stack>
              {promptUndo && promptDeleteConfirmIdx == null && !viewOnly ? (
                <PromptInlineAlert
                  variant="success"
                  message="Test prompt removed successfully."
                  actionLabel="Undo"
                  onAction={undoRemovePrompt}
                  onClose={dismissPromptRemovedToast}
                />
              ) : null}
              <Box sx={PROMPT_SECTION_LINE_SX} />
              <TestCaseHistorySection
                projectId={projectId}
                testCaseId={editingTestCase?.id}
                metrics={metrics}
                expectedLabel={expectedLabels.field}
              />
            </Box>
          </Stack>
        </Box>

        <Box sx={{ ...FLYOUT_FOOTER_SX, justifyContent: viewOnly ? "flex-end" : FLYOUT_FOOTER_SX.justifyContent }}>
          {!viewOnly ? (
            <Typography sx={FLYOUT_FOOTER_TEXT_SX}>
              {editingTestCase
                ? "Changes apply to this test case immediately after save."
                : "Test case will be created with confirmed status."}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "stretch", sm: "flex-end" },
              gap: "12px",
              flexShrink: 0,
              flexWrap: "wrap",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Button
              disableElevation
              disableRipple
              variant="outlined"
              onClick={onClose}
              sx={FLYOUT_CANCEL_BTN_SX}
            >
              {viewOnly ? "Close" : "Cancel"}
            </Button>
            {!viewOnly ? (
            <Button
              disableElevation
              disableRipple
              variant="contained"
              onClick={handleSave}
              disabled={submitting || !canSave}
              sx={FLYOUT_PRIMARY_BTN_SX}
            >
              {editingTestCase ? "Save changes" : "Add test case"}
            </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default function AddTestCaseDialog(props) {
  const isGeneratedEdit = Boolean(props.editingTestCase) && props.editingTestCase.source !== "Custom";
  if (isGeneratedEdit) return <GeneratedTestCaseEditDrawer {...props} />;
  return <AddOrCustomTestCaseDialog {...props} />;
}
